import React, { useEffect, useLayoutEffect, useRef } from 'react';
import './AuthenticatedLayout.scss';
import Disclaimer from '$/components/disclaimer';
import CutoverToCreditSights from '$/components/cutover-to-credit-sights';
import TopNavContainer from '$/components/global/topnav';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import { getSettings, acknowledgeDisclaimer } from '$/store/settings/actions';
import { ReactComponent as InfoIcon } from '$/assets/icons/info.svg';
import {
  getIsClientInPreviewFeatureFlagState,
  getIsClientIsCutoverToCreditSightsFeatureFlagState,
} from '$/store/login/selectors';

interface IAuthenticatedLayoutProps {
  dispatchAcknowledgeDisclaimer: () => Promise<void>;
  dispatchGetSettings: () => Promise<void>;
  welcomeMessage?: string;
  settings?: Bixby.Models.Settings;
  disclaimerAcknowledged: boolean;
  featureFlags: string[] | undefined;
  children?: React.ReactNode;
  settingsIsLoading: boolean;
  settingsError: Bixby.Models.BixbyError | undefined;
  isInPreview: boolean;
  isCutoverToCreditSights?: boolean;
  isAdmin?: boolean;
}

const AuthenticatedLayout: React.FC<IAuthenticatedLayoutProps> = props => {
  const {
    welcomeMessage,
    dispatchGetSettings,
    settings,
    disclaimerAcknowledged,
    dispatchAcknowledgeDisclaimer,
    settingsIsLoading,
    settingsError,
    isInPreview,
    isCutoverToCreditSights,
    isAdmin,
  } = props;

  useEffect(() => {
    const getSettingsAsync = async () => {
      await dispatchGetSettings();
    };
    if (typeof dispatchGetSettings !== undefined && !settingsIsLoading && !settings && settingsError === undefined) {
      getSettingsAsync();
    }
  }, [dispatchGetSettings, settingsIsLoading, settings, settingsError]);

  const handleSetDisclaimer = () => {
    dispatchAcknowledgeDisclaimer();
  };

  let conditionalLinkProps = {};
  if (!Bixby.Configuration.isStandAloneMode) {
    conditionalLinkProps = {
      target: '_blank',
    };
  }

  const bannerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const h = bannerRef.current?.getBoundingClientRect().height ?? 0;
    if (isInPreview) {
      document.documentElement.style.setProperty('--preview-banner-height', `${Math.ceil(h)}px`);
    } else {
      document.documentElement.style.removeProperty('--preview-banner-height');
    }
  }, [isInPreview]);

  return (
    <React.Fragment>
      {Bixby.Configuration.enableBuildNumberInUI && <div className="buildVersion">{Bixby.Configuration.version}</div>}

      {isInPreview && (
        <div
          ref={bannerRef}
          style={{
            padding: '5px',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            color: 'black',
            background: '#FFCC66',
          }}>
          <InfoIcon style={{ width: '18px', marginRight: 10 }} />
          <span style={{ paddingLeft: 2 }}>
            Bixby is moving to CreditSights on March 31st, 2026! Click{' '}
            <a href={Bixby.Configuration.creditSightsUrl} {...conditionalLinkProps}>
              <u>here</u>
            </a>{' '}
            to preview the new integrated site.
          </span>
        </div>
      )}

      <TopNavContainer welcomeMessage={welcomeMessage} helpEmail={Bixby.Configuration.mailtoHelp} />

      <main className="main_layout">{props.children}</main>
      {!isAdmin && isCutoverToCreditSights && <CutoverToCreditSights />}
      {settings && !isCutoverToCreditSights && (
        <>
          <Disclaimer
            modalTitle="Disclaimer"
            modalBody={settings ? settings.disclaimer : null}
            modalAcceptButtonText="Acknowledge and Accept"
            disclaimerAcknowledged={disclaimerAcknowledged}
            onCloseDisclaimer={handleSetDisclaimer}
            onAcknowledgeDisclaimer={handleSetDisclaimer}
            hideCloseButton={true}
          />
        </>
      )}
    </React.Fragment>
  );
};

function mapStateToProps(state: AppState) {
  const { settingsState, profileState, loginState } = state;

  const welcomeMessage =
    profileState && profileState.profile && profileState.profile.firstName
      ? Bixby.Constants.NAV_WELCOME_MESSAGE.replace('{0}', profileState.profile.firstName)
      : undefined;
  const settings = settingsState.settings;
  const disclaimerAcknowledged = settingsState.disclaimerAcknowledged;
  const featureFlags = loginState.featureFlags;
  const settingsIsLoading = settingsState.isLoading;
  const settingsError = settingsState.error;
  const { isInPreview } = getIsClientInPreviewFeatureFlagState(state);
  const { isCutoverToCreditSights } = getIsClientIsCutoverToCreditSightsFeatureFlagState(state);
  const isAdmin = state.profileState.profile?.isAdmin;

  return {
    settings,
    welcomeMessage,
    disclaimerAcknowledged,
    featureFlags,
    settingsIsLoading,
    settingsError,
    isInPreview,
    isCutoverToCreditSights,
    isAdmin,
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    dispatchGetSettings: () => dispatch(getSettings()),
    dispatchAcknowledgeDisclaimer: () => dispatch(acknowledgeDisclaimer()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AuthenticatedLayout);
